import { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Upload, Send, FileText, Trash2, Save, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";

import { api, formatErr } from "@/lib/api";
import { AREAS, CLASSIFICACOES, PESTEL, RECORTES, ACCESS_STATUS, LINHAS_PESQUISA_SFD } from "@/lib/constants";
import { computeCadastroState } from "@/lib/cadastroValidation";
import { useAuth } from "@/contexts/AuthContext";

import DatePickerBR from "@/components/DatePickerBR";
import ChecklistOperacional, { checklistTotals } from "@/components/ChecklistOperacional";
import ClassificacaoTooltip from "@/components/ClassificacaoTooltip";
import CitacaoDireta from "@/components/CitacaoDireta";
import InsumoCardStack from "@/components/InsumoCardStack";
import ModuleHelp from "@/components/help/ModuleHelp";

import MethodologyStepperV2 from "@/components/cadastro/MethodologyStepperV2";
import PendenciasPanel from "@/components/cadastro/PendenciasPanel";
import CadastroBlock from "@/components/cadastro/CadastroBlock";
import AiFieldBadge from "@/components/cadastro/AiFieldBadge";
import ModeSelector from "@/components/cadastro/ModeSelector";
import HelpHint from "@/components/cadastro/HelpHint";
import IncompleteModal from "@/components/cadastro/IncompleteModal";
import PreviewBlock from "@/components/cadastro/PreviewBlock";
import WordCounter from "@/components/cadastro/WordCounter";
import AIDeclaration, { isAiDeclarationValid } from "@/components/cadastro/AIDeclaration";
import SementePreviewManualSFD from "@/components/cadastro/SementePreviewManualSFD";

const initial = {
  source: {
    titulo: "",
    autores: "",
    origem_fonte: "",
    instituicao: "",
    tipo_fonte: "artigo cientifico",
    data_publicacao: "",
    link_original: "",
    link_drive: "",
    doi: "",
    idioma: "pt",
    pais_publicacao: "Brasil",
  },
  titulo_insumo: "",
  citacao_direta: "",
  horizonte_temporal: "2030-2040",
  impacto_2030_mais: true,
  classificacao_semente: "FPF",
  perspectiva_pestel: [],
  recorte_geografico: "Brasil",
  pais_impacto: "",
  area_tematica_principal: "GLO",
  areas_tematicas_secundarias: [],
  justificativa_classificacao: "",
  observacoes: "",
  pagina_ou_secao: "",
  trecho_traduzido_pt: "",
  access_status: "PENDENTE_VERIFICACAO",
  access_notes: "",
  link_drive_excepcional: "",
  researcher_checklist: {},
  methodology_stage: {},
  // ─── Manual SFD 2026 ────────────────────────────────────────────
  descricao_semente: "",
  impacto_futuro_defesa: "",
  palavras_chave_sfd: ["", "", "", "", ""],
  linha_pesquisa_sfd: "",
  pesquisador_display: "",
  ai_used: false,
  ai_tool_name: "",
  ai_usage_purpose: "",
  ai_human_verified: false,
};

// Campos que recebem badge de IA (mapeia para chave em aiFields)
const AI_TRACKED_FIELDS = [
  "source.titulo", "source.data_publicacao", "source.autores", "source.origem_fonte",
  "source.link_original", "source.tipo_fonte", "source.idioma", "source.pais_publicacao",
  "titulo_insumo", "citacao_direta", "horizonte_temporal", "classificacao_semente",
  "perspectiva_pestel", "recorte_geografico", "pais_impacto", "area_tematica_principal",
  "areas_tematicas_secundarias", "justificativa_classificacao", "observacoes",
];

export default function Cadastro() {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [insumosSugeridos, setInsumosSugeridos] = useState([]);
  const [extractBusy, setExtractBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  // Mapa { fieldKey: "suggested" | "reviewed" } - campos preenchidos por IA
  const [aiFields, setAiFields] = useState({});
  // ─── Modo e Wizard ───────────────────────────────────────────────────
  const [mode, setMode] = useState(() => {
    try {
      const saved = localStorage.getItem("cadastro:mode");
      return ["complete", "wizard", "sfd"].includes(saved) ? saved : "complete";
    } catch (_e) { return "complete"; }
  });
  const [wizardStep, setWizardStep] = useState(0);
  const [incompleteOpen, setIncompleteOpen] = useState(false);
  const fileInputRef = useRef(null);
  const nav = useNavigate();
  const { user } = useAuth();

  // Persiste modo no localStorage
  useEffect(() => {
    try {
      localStorage.setItem("cadastro:mode", mode);
    } catch (e) {
      console.warn("Falha ao persistir modo do cadastro no localStorage:", e);
    }
  }, [mode]);

  // Estado calculado (etapas, pendências, progresso)
  const formWithFlags = useMemo(
    () => ({ ...form, has_pdf: !!file || !!form.source.pdf_filename }),
    [form, file],
  );
  const state = useMemo(() => computeCadastroState(formWithFlags), [formWithFlags]);

  const updateSource = (k, v) => setForm({ ...form, source: { ...form.source, [k]: v } });
  const update = (k, v) => setForm({ ...form, [k]: v });

  // Quando o usuário edita um campo, marca como "revisado" se estava "sugerido"
  const reviewField = (key) =>
    setAiFields((prev) => (prev[key] === "suggested" ? { ...prev, [key]: "reviewed" } : prev));

  const handleSourceChange = (k, v) => {
    updateSource(k, v);
    reviewField(`source.${k}`);
  };
  const handleChange = (k, v) => {
    update(k, v);
    reviewField(k);
  };

  const togglePestel = (v) => {
    const cur = form.perspectiva_pestel;
    update("perspectiva_pestel", cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
    reviewField("perspectiva_pestel");
  };
  const toggleArea = (v) => {
    const cur = form.areas_tematicas_secundarias;
    update("areas_tematicas_secundarias", cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
    reviewField("areas_tematicas_secundarias");
  };

  const scrollTo = (anchor) => {
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const buildPayload = () => ({
    ...form,
    pais_impacto: (form.pais_impacto || "").split(",").map((s) => s.trim()).filter(Boolean),
    palavras_chave_sfd: (form.palavras_chave_sfd || []).map((k) => (k || "").trim()).filter(Boolean),
    source: {
      ...form.source,
      autores: (form.source.autores || "").split(",").map((s) => s.trim()).filter(Boolean),
    },
    origem_cadastro: aiPreview ? "upload_ia" : "manual",
    ai_processed: !!aiPreview,
    ai_processed_at: aiPreview ? new Date().toISOString() : null,
    ai_confidence_score: aiPreview ? Number(aiPreview.score_confianca_ia || 0) : null,
    source_file_name: uploadFile ? uploadFile.name : (file ? file.name : null),
  });

  // Marca todos os campos preenchidos pela IA como "suggested"
  const markAiSuggested = (changedKeys) => {
    setAiFields((prev) => {
      const next = { ...prev };
      changedKeys.forEach((k) => {
        if (AI_TRACKED_FIELDS.includes(k)) next[k] = "suggested";
      });
      return next;
    });
  };

  // V2 — extrai múltiplos insumos prospectivos do PDF/DOCX/TXT
  const runExtractInputs = async () => {
    if (!uploadFile) {
      setErr("Selecione um arquivo (PDF/DOCX/TXT) antes de extrair insumos.");
      return;
    }
    setErr(""); setInfo(""); setExtractBusy(true); setInsumosSugeridos([]);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      if (form.source.titulo) fd.append("titulo", form.source.titulo);
      if (form.source.origem_fonte) fd.append("origem", form.source.origem_fonte);
      if (form.source.link_original) fd.append("link", form.source.link_original);
      if (form.source.data_publicacao) fd.append("data", form.source.data_publicacao);
      const { data } = await api.post("/ai/extract-inputs-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fonte = data?.ai?.fonte || {};
      const insumos = data?.ai?.insumos || [];
      const changed = [];
      setForm((f) => {
        const next = { ...f, source: { ...f.source } };
        if (!f.source.titulo && fonte.titulo_sugerido) { next.source.titulo = fonte.titulo_sugerido; changed.push("source.titulo"); }
        if (!f.source.data_publicacao && fonte.data_sugerida) { next.source.data_publicacao = fonte.data_sugerida; changed.push("source.data_publicacao"); }
        if (!f.source.autores && fonte.autores?.length) { next.source.autores = fonte.autores.join(", "); changed.push("source.autores"); }
        if (!f.source.origem_fonte && fonte.origem_sugerida) { next.source.origem_fonte = fonte.origem_sugerida; changed.push("source.origem_fonte"); }
        if (fonte.tipo_fonte) { next.source.tipo_fonte = fonte.tipo_fonte; changed.push("source.tipo_fonte"); }
        if (fonte.idioma_original) { next.source.idioma = fonte.idioma_original; changed.push("source.idioma"); }
        return next;
      });
      markAiSuggested(changed);
      if (uploadFile.type === "application/pdf") setFile(uploadFile);
      setInsumosSugeridos(insumos);
      if (insumos.length === 0) {
        setInfo("A IA não identificou insumos prospectivos no documento.");
      } else {
        setInfo(`${insumos.length} insumo(s) prospectivo(s) identificado(s). Aceite ou descarte cada um.`);
      }
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setExtractBusy(false);
    }
  };

  const confirmBulkCreate = async (acceptedInsumos) => {
    if (!acceptedInsumos?.length) return;
    setErr(""); setInfo(""); setBulkBusy(true); setBulkResult(null);
    try {
      const sourcePayload = {
        titulo: form.source.titulo,
        autores: (form.source.autores || "").split(",").map((s) => s.trim()).filter(Boolean),
        origem_fonte: form.source.origem_fonte,
        tipo_fonte: form.source.tipo_fonte,
        data_publicacao: form.source.data_publicacao,
        link_original: form.source.link_original,
        doi: form.source.doi,
        idioma: form.source.idioma,
        pais_publicacao: form.source.pais_publicacao,
        instituicao: form.source.instituicao,
        access_status: form.access_status,
        access_notes: form.access_notes,
        link_drive: form.source.link_drive,
      };
      const { data: source } = await api.post("/sources", sourcePayload);
      const srcId = source.source_id;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        try {
          await api.post(`/sources/${srcId}/upload-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (uploadErr) {
          console.warn("Falha no upload do PDF da fonte (prosseguindo):", uploadErr);
        }
      }
      const created = [];
      const errors = [];
      for (const ins of acceptedInsumos) {
        try {
          const inputPayload = {
            source_id: srcId,
            titulo_insumo: ins.titulo_insumo || "(sem título)",
            trecho_literal: ins.trecho_literal || "",
            trecho_traduzido_pt: ins.trecho_traduzido_pt || "",
            pagina_ou_secao: ins.pagina_ou_secao || "",
            tipo_trecho: ins.tipo_trecho || "Parágrafo",
            resumo_do_insumo: ins.resumo_do_insumo || "",
            sinais_identificados: ins.sinais_identificados || [],
            fenomenos_relevantes: ins.fenomenos_relevantes || [],
            palavras_chave: ins.palavras_chave || [],
            classificacao_potencial_semente_sugerida: ins.classificacao_potencial_semente_sugerida || "FPF",
            perspectiva_pestel_sugerida: ins.perspectiva_pestel_sugerida || [],
            recorte_geografico_sugerido: ins.recorte_geografico_sugerido || "Brasil",
            pais_impacto_sugerido: ins.pais_impacto_sugerido || [],
            area_tematica_principal_sugerida: ins.area_tematica_principal_sugerida || "GLO",
            areas_tematicas_secundarias_sugeridas: ins.areas_tematicas_secundarias_sugeridas || [],
            horizonte_temporal: ins.horizonte_temporal || "2030-2040",
            impacto_a_partir_2030: ins.impacto_a_partir_2030 ?? true,
            ai_suggested: true,
            ai_confidence_score: Number(ins.score_confianca_ia || 0),
            justificativa: ins.justificativa || "",
          };
          const { data: inp } = await api.post("/inputs", inputPayload);
          const { data: seed } = await api.post(`/inputs/${inp.input_id}/promote-to-seed`, {
            researcher_checklist: form.researcher_checklist,
            methodology_stage: { ...form.methodology_stage, envio_revisao: "concluido", registro_formulario: "concluido" },
            access_status: form.access_status,
            link_drive_excepcional: form.link_drive_excepcional || null,
          });
          created.push({ input_id: inp.input_id, seed_id: seed.seed_id, titulo: ins.titulo_insumo });
        } catch (e) {
          errors.push({ titulo: ins.titulo_insumo, error: formatErr(e) });
        }
      }
      setBulkResult({ created, errors, source_id: srcId });
      if (created.length) {
        setInfo(`${created.length} Semente(s) criada(s) em status Pendente. ${errors.length ? `Falhas: ${errors.length}.` : ""}`);
        setTimeout(() => nav("/revisao"), 1500);
      } else {
        setErr(`Nenhuma semente criada. ${errors.length ? "Veja erros abaixo." : ""}`);
      }
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const clearUpload = () => {
    setUploadFile(null);
    setUploadStatus("");
    setAiPreview(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const applyAiToForm = (ai) => {
    const changed = [];
    setForm((f) => {
      const next = { ...f, source: { ...f.source } };
      if (ai.titulo_fonte) { next.source.titulo = ai.titulo_fonte; changed.push("source.titulo"); }
      if (ai.data_fonte) { next.source.data_publicacao = ai.data_fonte; changed.push("source.data_publicacao"); }
      if (ai.tipo_fonte) { next.source.tipo_fonte = ai.tipo_fonte; changed.push("source.tipo_fonte"); }
      if (ai.idioma_original) { next.source.idioma = ai.idioma_original; changed.push("source.idioma"); }
      if (ai.pais_publicacao) { next.source.pais_publicacao = ai.pais_publicacao; changed.push("source.pais_publicacao"); }
      if (ai.origem_fonte) { next.source.origem_fonte = ai.origem_fonte; changed.push("source.origem_fonte"); }
      if (ai.autores?.length) { next.source.autores = ai.autores.join(", "); changed.push("source.autores"); }
      if (ai.link_fonte) { next.source.link_original = ai.link_fonte; changed.push("source.link_original"); }
      if (ai.titulo_fonte) { next.titulo_insumo = ai.titulo_fonte; changed.push("titulo_insumo"); }
      if (ai.citacao_direta_sugerida) { next.citacao_direta = ai.citacao_direta_sugerida; changed.push("citacao_direta"); }
      if (ai.horizonte_temporal) { next.horizonte_temporal = ai.horizonte_temporal; changed.push("horizonte_temporal"); }
      if (ai.impacto_a_partir_2030 !== undefined) next.impacto_2030_mais = ai.impacto_a_partir_2030;
      if (ai.classificacao_potencial_semente) { next.classificacao_semente = ai.classificacao_potencial_semente; changed.push("classificacao_semente"); }
      if (ai.perspectiva_pestel?.length) { next.perspectiva_pestel = ai.perspectiva_pestel; changed.push("perspectiva_pestel"); }
      if (ai.recorte_geografico) { next.recorte_geografico = ai.recorte_geografico; changed.push("recorte_geografico"); }
      if (ai.pais_impacto?.length) { next.pais_impacto = ai.pais_impacto.join(", "); changed.push("pais_impacto"); }
      if (ai.area_tematica_principal) { next.area_tematica_principal = ai.area_tematica_principal; changed.push("area_tematica_principal"); }
      if (ai.areas_tematicas_secundarias?.length) { next.areas_tematicas_secundarias = ai.areas_tematicas_secundarias; changed.push("areas_tematicas_secundarias"); }
      if (ai.justificativa_classificacao) { next.justificativa_classificacao = ai.justificativa_classificacao; changed.push("justificativa_classificacao"); }
      if (ai.resumo_executivo) { next.observacoes = ai.resumo_executivo; changed.push("observacoes"); }
      return next;
    });
    markAiSuggested(changed);
  };

  const readUploadWithAi = async () => {
    if (!uploadFile) {
      setErr("Selecione um arquivo (PDF, DOCX ou TXT) antes de ler com IA.");
      return;
    }
    setErr(""); setInfo(""); setUploadStatus("analyzing"); setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const { data } = await api.post("/ai/analyze-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      applyAiToForm(data.ai);
      setAiPreview(data.ai);
      if (uploadFile.type === "application/pdf") setFile(uploadFile);
      setUploadStatus("done");
      setInfo("Campos preenchidos pela IA. Revise antes de salvar.");
    } catch (e) {
      setErr(formatErr(e));
      setUploadStatus("error");
    } finally {
      setAiBusy(false);
    }
  };

  const runAiPreview = async () => {
    setErr(""); setInfo(""); setAiBusy(true); setAiPreview(null);
    try {
      const text = form.citacao_direta || form.titulo_insumo || form.source.titulo;
      if (!text || text.length < 20) {
        throw new Error("Forneça pelo menos um título e uma citação direta com mais de 20 caracteres antes de analisar com IA.");
      }
      const { data } = await api.post("/ai/quick-analyze", {
        titulo: form.source.titulo,
        origem: form.source.origem_fonte,
        link: form.source.link_original,
        data: form.source.data_publicacao,
        texto: `${form.source.titulo}\n\n${form.citacao_direta}\n\n${form.observacoes}`,
      });
      setAiPreview(data);
      const changed = [];
      setForm((f) => {
        const next = { ...f };
        if (data.horizonte_temporal) { next.horizonte_temporal = data.horizonte_temporal; changed.push("horizonte_temporal"); }
        if (data.classificacao_potencial_semente) { next.classificacao_semente = data.classificacao_potencial_semente; changed.push("classificacao_semente"); }
        if (data.perspectiva_pestel?.length) { next.perspectiva_pestel = data.perspectiva_pestel; changed.push("perspectiva_pestel"); }
        if (data.recorte_geografico) { next.recorte_geografico = data.recorte_geografico; changed.push("recorte_geografico"); }
        if (data.area_tematica_principal) { next.area_tematica_principal = data.area_tematica_principal; changed.push("area_tematica_principal"); }
        if (data.areas_tematicas_secundarias?.length) { next.areas_tematicas_secundarias = data.areas_tematicas_secundarias; changed.push("areas_tematicas_secundarias"); }
        if (data.justificativa_classificacao) { next.justificativa_classificacao = data.justificativa_classificacao; changed.push("justificativa_classificacao"); }
        if (data.impacto_a_partir_2030 !== undefined) next.impacto_2030_mais = data.impacto_a_partir_2030;
        return next;
      });
      markAiSuggested(changed);
      setInfo("Sugestões da IA aplicadas. Revise e salve.");
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setAiBusy(false);
    }
  };

  // Upload do PDF associado à semente (envia junto do save)
  const uploadPdfAfterSave = async (seed) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      if (seed.source_id) {
        await api.post(`/sources/${seed.source_id}/upload-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post(`/seeds/${seed.seed_id}/upload-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
    } catch (_) {
      const fd2 = new FormData(); fd2.append("file", file);
      await api.post(`/seeds/${seed.seed_id}/upload-pdf`, fd2, { headers: { "Content-Type": "multipart/form-data" } });
    }
  };

  // SALVAR RASCUNHO — não valida obrigatórios, status = rascunho
  const saveDraft = async () => {
    if (!form.source.titulo && !form.titulo_insumo) {
      setErr("Para salvar como rascunho, informe ao menos o título da fonte ou o título do insumo.");
      return;
    }
    setErr(""); setInfo(""); setSavingDraft(true);
    try {
      const payload = {
        ...buildPayload(),
        methodology_stage: { ...form.methodology_stage, registro_formulario: "em_andamento" },
      };
      const { data: seed } = await api.post("/seeds?draft=true", payload);
      await uploadPdfAfterSave(seed);
      setInfo("Rascunho salvo. Você pode continuar editando na Biblioteca.");
      setTimeout(() => nav(`/biblioteca?status=rascunho`), 900);
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setSavingDraft(false);
    }
  };

  // ENVIAR PARA REVISÃO HUMANA — valida obrigatórios via state.canSubmit
  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr(""); setInfo("");
    if (state.pendencies.length > 0) {
      setIncompleteOpen(true);
      return;
    }
    if (!isAiDeclarationValid(form)) {
      setErr("Você declarou uso de IA mas não confirmou a revisão manual. Marque a caixa 'Confirmo que revisei manualmente' na seção Integridade & IA antes de enviar.");
      return;
    }
    const { done, total } = checklistTotals(form.researcher_checklist);
    if (done < total) {
      const ok = window.confirm(`Há ${total - done} item(ns) pendente(s) no checklist metodológico. Deseja continuar mesmo assim?`);
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = {
        ...buildPayload(),
        methodology_stage: { ...form.methodology_stage, registro_formulario: "concluido", envio_revisao: "concluido" },
      };
      const { data: seed } = await api.post("/seeds", payload);
      await uploadPdfAfterSave(seed);
      setInfo("Semente cadastrada como Pendente de Revisão.");
      setTimeout(() => nav("/biblioteca"), 700);
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setSaving(false);
    }
  };

  // ─── Wizard Navigation ───────────────────────────────────────────────
  const WIZARD_STEPS = [
    { id: "fonte", label: "Fonte", anchor: "block-fonte" },
    { id: "acesso", label: "Acesso", anchor: "block-acesso" },
    { id: "insumo", label: "Insumo", anchor: "block-insumo" },
    { id: "classificacao", label: "Classificação", anchor: "block-classificacao" },
    { id: "checklist", label: "Checklist", anchor: "block-checklist" },
    { id: "revisao", label: "Prévia & Envio", anchor: "block-revisao" },
  ];
  const activeStepId = (mode === "wizard" || mode === "sfd") ? WIZARD_STEPS[wizardStep].id : null;
  const isBlockVisible = (blockId) =>
    mode === "complete" || WIZARD_STEPS[wizardStep].id === blockId;

  const handleStepClick = (step) => {
    if (mode === "wizard" || mode === "sfd") {
      const idx = WIZARD_STEPS.findIndex((s) => s.id === step.id);
      if (idx >= 0) setWizardStep(idx);
    } else {
      const el = document.getElementById(step.anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const goPrevStep = () => setWizardStep((s) => Math.max(0, s - 1));
  const goNextStep = () => setWizardStep((s) => Math.min(WIZARD_STEPS.length - 1, s + 1));

  const stepByAnchor = (anchor) => state.steps.find((s) => s.anchor === anchor);

  return (
    <div className="space-y-6" data-testid="cadastro-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mil text-[var(--gold)]">Coleta Manual</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-3">
            Cadastro de Semente de Futuro <ModuleHelp module="cadastro" />
          </h1>
          <p className="text-[var(--text-muted)] text-sm mt-2 max-w-3xl">
            Todo registro nasce como <span className="text-[var(--warn)] font-mono">PENDENTE</span> e requer validação humana.
            Use a IA Gemini para auxílio na classificação, salve como <strong className="text-[var(--text)]">rascunho</strong> a
            qualquer momento ou envie diretamente para revisão.
          </p>
        </div>
        <ModeSelector value={mode} onChange={setMode} />
      </div>

      {/* Esteira Metodológica (sticky no topo) */}
      <div id="methodology-stepper" className="sticky top-2 z-30">
        <MethodologyStepperV2
          form={formWithFlags}
          onStepClick={handleStepClick}
          activeStepId={activeStepId}
        />
      </div>

      {/* Grid principal: conteúdo + sidebar */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4 items-start">
        <form
          onSubmit={submit}
          className="space-y-4 min-w-0"
          data-testid="cadastro-form"
        >
          {/* Bloco: Leitura automática com IA (só visível em modo completo ou etapa Fonte de wizard/sfd) */}
          {(mode === "complete" || wizardStep === 0) && (
          <div className="card-mil p-5 gold-accent-top space-y-4" data-testid="cadastro-upload-block">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display font-bold text-lg flex items-center gap-2">Leitura automática de artigo com IA <HelpHint term="Upload com IA" /></h3>
                <p className="text-xs text-[var(--text-muted)] mt-1 max-w-2xl">
                  Envie um PDF, DOCX ou TXT. A IA Gemini lerá o documento e preencherá os campos abaixo.
                  Os campos sugeridos aparecerão marcados com <em>Sugerido por IA</em> — clique para validar como
                  <em> Revisado pelo pesquisador</em>.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`badge ${
                  uploadStatus === "analyzing" ? "badge-warn" :
                  uploadStatus === "done" ? "badge-success" :
                  uploadStatus === "error" ? "badge-danger" :
                  uploadFile ? "badge-info" : "badge-muted"
                }`} data-testid="upload-state-badge">
                  {uploadStatus === "analyzing" ? "Processando com IA" :
                    uploadStatus === "done" ? "Campos preenchidos" :
                    uploadStatus === "error" ? "Falha na leitura" :
                    uploadFile ? "Arquivo carregado" : "Aguardando arquivo"}
                </span>
                <span className="badge badge-gold">Gemini 2.5-flash</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
              <label className="btn-ghost cursor-pointer inline-flex items-center justify-center gap-2 text-sm" data-testid="upload-input-label">
                <Upload size={14} /> {uploadFile ? "Trocar arquivo" : "Selecionar PDF, DOCX ou TXT"}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  className="hidden"
                  onChange={(e) => { setUploadFile(e.target.files?.[0] || null); setUploadStatus(""); setAiPreview(null); }}
                  data-testid="upload-input"
                />
              </label>
              <div className="text-xs font-mono text-[var(--text-muted)] truncate" data-testid="upload-fileinfo">
                {uploadFile ? (<><FileText size={12} className="inline mr-1" />{uploadFile.name} · {(uploadFile.size / 1024).toFixed(1)} KB</>) : "Nenhum arquivo selecionado"}
              </div>
              <button type="button" onClick={readUploadWithAi} disabled={!uploadFile || aiBusy} className="btn-gold text-sm flex items-center justify-center gap-2" data-testid="upload-read-ai">
                <Sparkles size={14} /> {aiBusy ? "ANALISANDO…" : "Analisar arquivo com IA"}
              </button>
              <button type="button" onClick={runExtractInputs} disabled={!uploadFile || extractBusy} className="btn-ghost text-sm flex items-center justify-center gap-2 border-[var(--gold)] text-[var(--gold)]" data-testid="upload-extract-inputs">
                <Sparkles size={14} /> {extractBusy ? "EXTRAINDO…" : "Sugerir insumos (V2)"}
              </button>
              <button type="button" onClick={clearUpload} disabled={!uploadFile && !aiPreview} className="btn-ghost text-sm flex items-center justify-center gap-2" data-testid="upload-clear">
                <Trash2 size={14} /> Limpar arquivo
              </button>
            </div>

            {uploadStatus === "analyzing" && (
              <div className="bg-[var(--surface-2)] border border-[var(--gold)]/30 rounded p-3 text-sm text-[var(--text)] flex items-center gap-3" data-testid="upload-progress">
                <div className="w-2 h-2 bg-[var(--gold)] rounded-full animate-pulse" />
                Analisando o documento e extraindo dados prospectivos. Isso pode levar alguns instantes.
              </div>
            )}

            {aiPreview && uploadStatus === "done" && (
              <div className="border-l-2 border-[var(--gold)] bg-[var(--surface-2)] p-3 rounded-r text-sm" data-testid="ai-review-notice">
                <strong>A IA preencheu os campos com base no documento enviado.</strong>{" "}
                Revise as informações antes de enviar para validação humana.
              </div>
            )}

            {aiPreview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono pt-2 border-t border-[var(--border)]" data-testid="upload-ai-summary">
                <div><span className="label-mil block">Score IA</span><span className="text-[var(--gold)] text-sm">{aiPreview.score_confianca_ia}</span></div>
                <div><span className="label-mil block">Relevância</span><span className="text-[var(--gold)] text-sm">{aiPreview.nivel_relevancia}</span></div>
                <div><span className="label-mil block">Classificação</span><span className="text-sm">{aiPreview.classificacao_potencial_semente}</span></div>
                <div><span className="label-mil block">Recomendação IA</span><span className="text-[var(--info)] text-sm">{aiPreview.recomendacao}</span></div>
              </div>
            )}
          </div>
          )}

          {/* V2 — Card stack de insumos sugeridos */}
          {insumosSugeridos.length > 0 && (
            <div>
              <InsumoCardStack insumos={insumosSugeridos} onConfirm={confirmBulkCreate} busy={bulkBusy} />
              {bulkResult && (
                <div className="card-mil p-3 mt-3" data-testid="bulk-result">
                  <div className="text-xs">
                    <span className="text-[var(--success)] font-mono">✓ {bulkResult.created.length} Semente(s) criada(s)</span>
                    {bulkResult.errors.length > 0 && (
                      <span className="text-[var(--danger)] font-mono ml-3">✗ {bulkResult.errors.length} falha(s)</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* BLOCO 1 — FONTE */}
          {isBlockVisible("fonte") && (
          <CadastroBlock
            id="block-fonte"
            step={stepByAnchor("block-fonte")}
            title="1 · Fonte de Informação"
            subtitle="Identificação bibliográfica básica: o quê, quem e quando."
            hint="Informe os dados bibliográficos principais do documento analisado."
          >
            <div>
              <FieldLabel required aiKey="source.titulo" aiFields={aiFields} setAiFields={setAiFields} helpTerm="Fonte">
                Título da fonte
              </FieldLabel>
              <input className="input-mil" required value={form.source.titulo} onChange={(e) => handleSourceChange("titulo", e.target.value)} data-testid="cadastro-titulo-fonte" />
              {mode === "sfd" && <WordCounter text={form.source.titulo} max={15} />}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel aiKey="source.data_publicacao" aiFields={aiFields} setAiFields={setAiFields}>
                  Data da fonte
                </FieldLabel>
                <DatePickerBR value={form.source.data_publicacao} onChange={(v) => handleSourceChange("data_publicacao", v)} testid="cadastro-data-fonte" />
              </div>
              <div>
                <FieldLabel aiKey="source.idioma" aiFields={aiFields} setAiFields={setAiFields}>Idioma</FieldLabel>
                <select className="input-mil" value={form.source.idioma} onChange={(e) => handleSourceChange("idioma", e.target.value)}>
                  <option value="pt">Português</option>
                  <option value="en">Inglês</option>
                  <option value="es">Espanhol</option>
                  <option value="fr">Francês</option>
                </select>
              </div>
              <div>
                <FieldLabel aiKey="source.tipo_fonte" aiFields={aiFields} setAiFields={setAiFields}>Tipo de fonte</FieldLabel>
                <select className="input-mil" value={form.source.tipo_fonte} onChange={(e) => handleSourceChange("tipo_fonte", e.target.value)} data-testid="cadastro-tipo-fonte">
                  <option value="artigo cientifico">Artigo Científico</option>
                  <option value="relatorio">Relatório Técnico</option>
                  <option value="livro">Livro</option>
                  <option value="palestra">Palestra</option>
                  <option value="literatura cinzenta">Literatura Cinzenta</option>
                  <option value="documento institucional">Documento Institucional</option>
                </select>
              </div>
              <div>
                <FieldLabel aiKey="source.pais_publicacao" aiFields={aiFields} setAiFields={setAiFields}>País de publicação</FieldLabel>
                <input className="input-mil" value={form.source.pais_publicacao} onChange={(e) => handleSourceChange("pais_publicacao", e.target.value)} />
              </div>
              <div className="col-span-2">
                <FieldLabel aiKey="source.origem_fonte" aiFields={aiFields} setAiFields={setAiFields}>
                  Origem (instituição, revista, autor)
                </FieldLabel>
                <input className="input-mil" value={form.source.origem_fonte} onChange={(e) => handleSourceChange("origem_fonte", e.target.value)} />
              </div>
              <div className="col-span-2">
                <FieldLabel aiKey="source.autores" aiFields={aiFields} setAiFields={setAiFields}>
                  Autores (separados por vírgula)
                </FieldLabel>
                <input className="input-mil" value={form.source.autores} onChange={(e) => handleSourceChange("autores", e.target.value)} data-testid="cadastro-autores-fonte" />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button type="button" onClick={() => scrollTo("block-acesso")} className="text-xs text-[var(--gold)] hover:underline inline-flex items-center gap-1">
                Próximo: Acesso <ArrowDown size={12} />
              </button>
            </div>
          </CadastroBlock>
          )}

          {/* BLOCO 2 — ACESSO */}
          {isBlockVisible("acesso") && (
          <CadastroBlock
            id="block-acesso"
            step={stepByAnchor("block-acesso")}
            title="2 · Acesso à Fonte"
            subtitle="Garante rastreabilidade do documento: link público, DOI e/ou PDF anexo."
            hint="Registre link original, DOI, PDF ou Drive excepcional."
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel aiKey="source.link_original" aiFields={aiFields} setAiFields={setAiFields}>Link original</FieldLabel>
                <input className="input-mil" value={form.source.link_original} onChange={(e) => handleSourceChange("link_original", e.target.value)} />
              </div>
              <div>
                <label className="label-mil block mb-1">DOI</label>
                <input className="input-mil" value={form.source.doi} onChange={(e) => updateSource("doi", e.target.value)} />
              </div>
              <div>
                <label className="label-mil block mb-1 flex items-center gap-2">Link Drive (excepcional) <HelpHint term="Link Drive Excepcional" /></label>
                <input className="input-mil" value={form.source.link_drive} onChange={(e) => updateSource("link_drive", e.target.value)} />
                <div className="text-[10px] text-[var(--text-muted)] mt-1">Use somente se não for anexar PDF.</div>
              </div>
              <div>
                <label className="label-mil block mb-1">Status do Acesso</label>
                <select className="input-mil" value={form.access_status} onChange={(e) => update("access_status", e.target.value)} data-testid="access-status-select">
                  {ACCESS_STATUS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
                <div className="text-[10px] text-[var(--text-muted)] mt-1">
                  {form.access_status === "LINK_QUEBRADO" ? "Sugestão: buscar no Google Acadêmico ou anexar PDF." :
                    form.access_status === "SEM_LINK" ? "Sugestão: anexar PDF ou usar Link Drive excepcional." :
                      form.access_status === "LINK_FUNCIONANDO" ? "Link OK — pode acessar diretamente." :
                        "Verificação do acesso à fonte (método V2)."}
                </div>
              </div>
              <div className="col-span-2">
                <label className="label-mil block mb-1">Upload PDF da fonte</label>
                <label className="btn-ghost cursor-pointer inline-flex items-center gap-2 text-sm" data-testid="cadastro-pdf-button">
                  <Upload size={14} /> {file ? file.name : "Selecionar PDF (até 8MB)"}
                  <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
              </div>
            </div>
          </CadastroBlock>
          )}

          {/* BLOCO 3 — INSUMO */}
          {isBlockVisible("insumo") && (
          <CadastroBlock
            id="block-insumo"
            step={stepByAnchor("block-insumo")}
            title="3 · Insumo Prospectivo"
            subtitle="Trecho específico do documento que será analisado como uma Semente de Futuro."
            hint="Extraia o trecho que indica mudança, tendência, risco emergente ou transformação relevante."
          >
            <div>
              <FieldLabel required aiKey="titulo_insumo" aiFields={aiFields} setAiFields={setAiFields} helpTerm="Insumo">
                Título do insumo / semente
              </FieldLabel>
              <input className="input-mil" required value={form.titulo_insumo} onChange={(e) => handleChange("titulo_insumo", e.target.value)} data-testid="cadastro-titulo-insumo" />
            </div>
            <CitacaoDireta
              value={form.citacao_direta}
              onChange={(v) => handleChange("citacao_direta", v)}
              paginaOuSecao={form.pagina_ou_secao}
              onChangePagina={(v) => update("pagina_ou_secao", v)}
              trechoTraduzidoPt={form.trecho_traduzido_pt}
              onChangeTrecho={(v) => update("trecho_traduzido_pt", v)}
            />

            {/* Campos exclusivos do Manual SFD 2026 */}
            {mode === "sfd" && (
              <div className="pt-3 mt-3 border-t border-dashed border-[var(--gold)]/40 space-y-4" data-testid="sfd-extra-insumo">
                <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-[var(--gold)] font-semibold">
                  Manual SFD 2026 · Redação da Semente
                </div>

                <div>
                  <label className="label-mil block mb-1">Descrição da Semente</label>
                  <textarea
                    className="input-mil"
                    rows={4}
                    value={form.descricao_semente}
                    onChange={(e) => handleChange("descricao_semente", e.target.value)}
                    placeholder="Resumo descritivo da semente apontada, com autoria própria."
                    data-testid="sfd-descricao"
                  />
                  <WordCounter text={form.descricao_semente} max={150} />
                </div>

                <div>
                  <label className="label-mil block mb-1">Impactos Futuros em Defesa</label>
                  <textarea
                    className="input-mil"
                    rows={3}
                    value={form.impacto_futuro_defesa}
                    onChange={(e) => handleChange("impacto_futuro_defesa", e.target.value)}
                    placeholder="Descreva o impacto futuro. Não expandir a descrição, não recomendar, não opinar."
                    data-testid="sfd-impacto"
                  />
                  <WordCounter text={form.impacto_futuro_defesa} max={100} />
                </div>

                <div>
                  <label className="label-mil block mb-1">Palavras-chave (5 recomendadas)</label>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                    {form.palavras_chave_sfd.map((kw, idx) => (
                      <input
                        key={`sfd-kw-input-${idx}`}
                        className="input-mil text-sm"
                        value={kw}
                        onChange={(e) => {
                          const next = [...form.palavras_chave_sfd];
                          next[idx] = e.target.value;
                          update("palavras_chave_sfd", next);
                        }}
                        placeholder={`kw ${idx + 1}`}
                        data-testid={`sfd-kw-${idx}`}
                      />
                    ))}
                  </div>
                  <div className="text-[11px] font-mono text-[var(--text-muted)] mt-1" data-testid="sfd-kw-counter">
                    {form.palavras_chave_sfd.filter((k) => (k || "").trim()).length} de 5 palavras-chave preenchidas
                  </div>
                </div>
              </div>
            )}
          </CadastroBlock>
          )}

          {/* BLOCO 4 — CLASSIFICAÇÃO */}
          {isBlockVisible("classificacao") && (
          <CadastroBlock
            id="block-classificacao"
            step={stepByAnchor("block-classificacao")}
            title="4 · Classificação Prospectiva"
            subtitle="Categorização metodológica (LSC/EGN)."
            hint="Classifique o insumo conforme os vetores LSC, PESTEL e categorias prospectivas."
          >
            {/* AI Quick Analyze inline */}
            <div className="flex items-center justify-between border border-[var(--gold)]/30 bg-[var(--surface-2)] rounded p-2.5 gap-3 flex-wrap">
              <div className="text-xs text-[var(--text-muted)]">
                <span className="badge badge-info mr-2">Gemini 2.5-flash</span>
                Use o texto da citação para sugerir classificação, PESTEL, área e horizonte.
              </div>
              <button type="button" onClick={runAiPreview} disabled={aiBusy} className="btn-gold text-sm flex items-center gap-2" data-testid="cadastro-ai-button">
                <Sparkles size={14} /> {aiBusy ? "ANALISANDO…" : "Analisar com IA"}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel required aiKey="classificacao_semente" aiFields={aiFields} setAiFields={setAiFields} helpTerm="Semente de Futuro">
                  Classificação da semente
                  <ClassificacaoTooltip current={form.classificacao_semente} />
                </FieldLabel>
                <select className="input-mil" value={form.classificacao_semente} onChange={(e) => handleChange("classificacao_semente", e.target.value)} data-testid="cadastro-classificacao">
                  {CLASSIFICACOES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel required aiKey="area_tematica_principal" aiFields={aiFields} setAiFields={setAiFields}>
                  Área temática principal
                </FieldLabel>
                <select className="input-mil" value={form.area_tematica_principal} onChange={(e) => handleChange("area_tematica_principal", e.target.value)} data-testid="cadastro-area">
                  {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              {mode === "sfd" && (
                <div className="md:col-span-2" data-testid="sfd-linha-container">
                  <label className="label-mil block mb-1">Linha de Pesquisa SFD 2026</label>
                  <select
                    className="input-mil"
                    value={form.linha_pesquisa_sfd}
                    onChange={(e) => update("linha_pesquisa_sfd", e.target.value)}
                    data-testid="sfd-linha-select"
                  >
                    <option value="">— selecione a linha do Manual —</option>
                    {LINHAS_PESQUISA_SFD.map((l) => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-[var(--text-muted)] mt-1">
                    Uma das 14 linhas oficiais do Manual Sementes de Futuro em Defesa 2026.
                    Coexiste com a Área Temática do Nostradamus.
                  </div>
                </div>
              )}
              <div className="md:col-span-2">
                <FieldLabel aiKey="perspectiva_pestel" aiFields={aiFields} setAiFields={setAiFields} helpTerm="PESTEL">
                  Perspectiva PESTEL (múltipla)
                </FieldLabel>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                  {PESTEL.map((p) => (
                    <button key={p.value} type="button" onClick={() => togglePestel(p.value)}
                      className={`text-xs px-2 py-1.5 border rounded font-mono ${form.perspectiva_pestel.includes(p.value) ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--border)] text-[var(--text-muted)]"}`}
                      data-testid={`pestel-${p.value}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <FieldLabel aiKey="recorte_geografico" aiFields={aiFields} setAiFields={setAiFields} helpTerm="Recorte Geográfico">Recorte geográfico</FieldLabel>
                <select className="input-mil" value={form.recorte_geografico} onChange={(e) => handleChange("recorte_geografico", e.target.value)}>
                  {RECORTES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel aiKey="pais_impacto" aiFields={aiFields} setAiFields={setAiFields}>
                  Países de impacto (vírgula)
                </FieldLabel>
                <input className="input-mil" value={form.pais_impacto} onChange={(e) => handleChange("pais_impacto", e.target.value)} placeholder="Brasil, Argentina" />
              </div>
              <div>
                <FieldLabel aiKey="horizonte_temporal" aiFields={aiFields} setAiFields={setAiFields} helpTerm="Horizonte Temporal">
                  Horizonte temporal
                </FieldLabel>
                <input className="input-mil" value={form.horizonte_temporal} onChange={(e) => handleChange("horizonte_temporal", e.target.value)} placeholder="ex: 2030-2040" />
              </div>
              <div>
                <label className="label-mil block mb-1">Impacto a partir de 2030</label>
                <select className="input-mil" value={form.impacto_2030_mais ? "1" : "0"} onChange={(e) => update("impacto_2030_mais", e.target.value === "1")}>
                  <option value="1">Sim</option>
                  <option value="0">Não</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-mil block mb-2">Áreas secundárias</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto scrollbar-thin">
                  {AREAS.map((a) => (
                    <button key={a.value} type="button" onClick={() => toggleArea(a.value)}
                      className={`text-[10px] px-2 py-1 border rounded font-mono text-left ${form.areas_tematicas_secundarias.includes(a.value) ? "border-[var(--info)] text-[var(--info)] bg-[var(--info)]/10" : "border-[var(--border)] text-[var(--text-muted)]"}`}>
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <FieldLabel aiKey="justificativa_classificacao" aiFields={aiFields} setAiFields={setAiFields}>
                  Justificativa
                </FieldLabel>
                <textarea className="input-mil" rows={3} value={form.justificativa_classificacao} onChange={(e) => handleChange("justificativa_classificacao", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <FieldLabel aiKey="observacoes" aiFields={aiFields} setAiFields={setAiFields}>Observações</FieldLabel>
                <textarea className="input-mil" rows={2} value={form.observacoes} onChange={(e) => handleChange("observacoes", e.target.value)} />
              </div>
            </div>
          </CadastroBlock>
          )}

          {/* BLOCO EXTRA — INTEGRIDADE & IA (só modo SFD, junto da etapa Checklist) */}
          {mode === "sfd" && isBlockVisible("checklist") && (
            <div className="card-mil p-5 space-y-4" data-testid="block-integridade-ia">
              <div>
                <h3 className="font-display font-bold text-lg">Integridade & Uso de IA</h3>
                <p className="text-[12px] text-[var(--text-muted)] mt-1 max-w-2xl">
                  Declaração obrigatória conforme o Manual SFD 2026 quando houver uso de IA
                  no preenchimento desta Semente.
                </p>
              </div>
              <AIDeclaration form={form} onChange={setForm} />
            </div>
          )}

          {/* BLOCO 5 — CHECKLIST */}
          {isBlockVisible("checklist") && (
          <CadastroBlock
            id="block-checklist"
            step={stepByAnchor("block-checklist")}
            title="5 · Checklist Metodológico"
            subtitle="Garantia de rigor científico antes da revisão humana."
            hint="Confirme os critérios mínimos antes de enviar para revisão."
          >
            <ChecklistOperacional
              value={form.researcher_checklist}
              onChange={(v) => update("researcher_checklist", v)}
            />
          </CadastroBlock>
          )}

          {/* BLOCO 6 — PRÉVIA & ENVIO */}
          {isBlockVisible("revisao") && (
          <CadastroBlock
            id="block-revisao"
            step={stepByAnchor("block-revisao")}
            title="6 · Prévia & Envio"
            subtitle="Confira o resumo, salve como rascunho ou envie para revisão humana."
            hint={state.canSubmit ? "Tudo pronto — pode enviar para revisão." : "Resolva as pendências do painel à direita antes de enviar para revisão."}
          >
            {mode === "wizard" && (
              <PreviewBlock form={form} state={state} user={user} file={file} />
            )}
            {mode === "sfd" && (
              <SementePreviewManualSFD form={form} user={user} file={file} />
            )}

            {err && <div className="text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10 p-3 rounded" data-testid="cadastro-error">{err}</div>}
            {info && <div className="text-sm text-[var(--success)] border border-[var(--success)]/40 bg-[var(--success)]/10 p-3 rounded" data-testid="cadastro-info">{info}</div>}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={saveDraft}
                disabled={savingDraft || saving}
                className="btn-ghost flex-1 flex items-center justify-center gap-2"
                data-testid="cadastro-save-draft-button"
              >
                <Save size={14} /> {savingDraft ? "SALVANDO…" : "Salvar como rascunho"}
              </button>
              <button
                type="submit"
                disabled={saving || savingDraft || !state.canSubmit}
                className="btn-gold flex-1 flex items-center justify-center gap-2"
                data-testid="cadastro-submit-button"
                title={!state.canSubmit ? "Resolva pendências obrigatórias antes de enviar" : "Enviar para Revisão Humana"}
              >
                <Send size={14} /> {saving ? "ENVIANDO…" : "Enviar para Revisão Humana"}
              </button>
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-2">
              <strong>Rascunho:</strong> não passa pela revisão, fica disponível só para você editar.{" "}
              <strong>Enviar para Revisão:</strong> bloqueia novas edições e segue para coordenador/revisor.
            </div>
          </CadastroBlock>
          )}

          {/* Navegação do Wizard (Voltar / Próximo / Salvar Rascunho) — vale para wizard E sfd */}
          {(mode === "wizard" || mode === "sfd") && (
            <div
              className="card-mil p-3 flex items-center gap-2 flex-wrap justify-between"
              data-testid="wizard-nav"
            >
              <button
                type="button"
                onClick={goPrevStep}
                disabled={wizardStep === 0}
                className="btn-ghost text-sm flex items-center gap-2"
                data-testid="wizard-prev"
              >
                <ArrowLeft size={14} /> Voltar
              </button>
              <div className="text-xs text-[var(--text-muted)]">
                Etapa <strong className="text-[var(--text)]">{wizardStep + 1}</strong> de {WIZARD_STEPS.length} · {WIZARD_STEPS[wizardStep].label}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={saveDraft}
                  disabled={savingDraft || saving}
                  className="btn-ghost text-sm flex items-center gap-2"
                  data-testid="wizard-save-draft"
                >
                  <Save size={14} /> {savingDraft ? "Salvando…" : "Salvar rascunho"}
                </button>
                {wizardStep < WIZARD_STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={goNextStep}
                    className="btn-gold text-sm flex items-center gap-2"
                    data-testid="wizard-next"
                  >
                    Próximo <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={saving || savingDraft || !state.canSubmit}
                    className="btn-gold text-sm flex items-center gap-2"
                    data-testid="wizard-submit"
                    title={!state.canSubmit ? "Resolva pendências obrigatórias antes de enviar" : "Enviar para Revisão Humana"}
                  >
                    <Send size={14} /> {saving ? "Enviando…" : "Enviar para Revisão Humana"}
                  </button>
                )}
              </div>
            </div>
          )}
        </form>

        {/* Sidebar: Painel de Pendências */}
        <PendenciasPanel form={formWithFlags} onJump={(anchor) => {
          if (mode === "wizard" || mode === "sfd") {
            const stepId = anchor.replace("block-", "");
            const idx = WIZARD_STEPS.findIndex((s) => s.id === stepId);
            if (idx >= 0) setWizardStep(idx);
          } else {
            scrollTo(anchor);
          }
        }} />
      </div>

      {/* Modal de cadastro incompleto */}
      <IncompleteModal
        open={incompleteOpen}
        pendencies={state.pendencies}
        onClose={() => setIncompleteOpen(false)}
        onSaveDraft={async () => {
          setIncompleteOpen(false);
          await saveDraft();
        }}
        savingDraft={savingDraft}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// FieldLabel — label com badge "Sugerido por IA" / "Revisado"
// ────────────────────────────────────────────────────────────────────────────
function FieldLabel({ children, required, aiKey, aiFields, setAiFields, helpTerm }) {
  const status = aiKey ? aiFields[aiKey] : null;
  return (
    <label className="label-mil block mb-1 flex items-center gap-2 flex-wrap">
      <span className="flex items-center gap-1.5">
        {children} {required && <span className="text-[var(--danger)]">*</span>}
        {helpTerm && <HelpHint term={helpTerm} />}
      </span>
      {status && (
        <AiFieldBadge
          status={status}
          onReview={() => setAiFields((p) => ({ ...p, [aiKey]: "reviewed" }))}
          testid={`ai-badge-${aiKey.replace(/\./g, "-")}`}
        />
      )}
    </label>
  );
}
